
import React, { useState, useEffect } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { 
  Save, 
  ArrowLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';

const backendUrl = 'https://justurbane.in/backend_php/public';

const AdmincarouselEditorPage = () => {

  const [carousel, setCarousel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const { carouselId } = useParams();

  // When carousel.body changes (load or API fetch), sync content once
  useEffect(() => {
    // Check admin authentication
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    if (carouselId && carouselId !== 'new') {
      fetchCarousel();
    } else {
      // New carousel
      setCarousel({
        id: null, 
        title: '', 
        description:'', 
        category: '', 
        image_url: '', 
      });
      setLoading(false);
    }
  }, [navigate, carouselId]);

  const categories = [
    'fashion', 'people', 'business', 'technology', 'lifestyle',
    'travel', 'culture', 'art', 'entertainment', 'health', 'finance', 'automotive'
  ];

  const fetchCarousel = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(
        `${backendUrl}/api/admin/carousel/${carouselId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setCarousel({
          ...data,
        });
      } else {
        throw new Error('Failed to fetch carousel');
      }
    } catch (error) {
      toast.error('Failed to load carousel');
      console.error('Carousel fetch error:', error);
      navigate('/admin/carousel');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!carousel.title || !carousel.description || !carousel.category) {
      toast.error('Please fill required fields');
      return;
    }
  
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();

      formData.append('title', carousel.title);
      formData.append('description', carousel.description);
      formData.append('category', carousel.category);
      
      if (carousel.image_url instanceof File) {
        formData.append('image_url', carousel.image_url);
      }
  
      const url = `${backendUrl}/api/admin/carousel/${carousel.id}`;
  
      const method = 'POST';
      formData.append('_method', 'PUT');

  
      const response = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
  
      if (response.ok) {
        const result = await response.json();
        toast.success('Carousel updated!');
        navigate(`/admin/carousel`);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Update failed');
      }
    } catch (error) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setSaving(false);
    }
  };
      
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading carousel editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/carousel')}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Carousels</span>
              </button>
              <div className="h-6 w-px bg-gray-300"></div>
              <h1 className="text-xl font-bold text-gray-900">
                {carousel?.id ? 'Edit Carousel' : 'Create New Carousel'}
              </h1>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center space-x-2 bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>
                  {saving ? (
                    carousel?.id ? 'Updating...' : 'Saving...'
                  ) : 
                    carousel?.id ? 'Update' : 'Save'
                  }
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content Editor */}
          <div className="lg:col-span-3 space-y-6">
            {/* Title and description */}
            <div className="bg-white rounded-xl shadow-sm border p-6">
                <div className="space-y-4">

                {/* ✅ Title and Category in one row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                        Carousel Title *
                        </label>
                        <input
                        type="text"
                        value={carousel?.title || ''}
                        onChange={(e) => setCarousel({ ...carousel, title: e.target.value })}
                        className="w-full px-3 py-2 text-xl font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 h-[52px]"
                        placeholder="Enter carousel title..."
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                        Category *
                        </label>
                        <select
                        value={carousel?.category || ''}
                        onChange={(e) => setCarousel({ ...carousel, category: e.target.value, subcategory: '' })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 h-[52px]"
                        >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                            <option key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                            </option>
                        ))}
                        </select>
                    </div>
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                    </label>
                    <textarea
                    value={carousel?.description || ''}
                    onChange={(e) => setCarousel({ ...carousel, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="Brief carousel description..."
                    />
                </div>

                {/* Hero Image Input */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image
                    </label>
                    <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                        setCarousel({ ...carousel, image_url: file });
                        }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    />

                    {/* Preview */}
                    {carousel.image_url && (
                    <div className="mt-2">
                        <img
                        src={
                            carousel.image_url instanceof File
                            ? URL.createObjectURL(carousel.image_url)
                            : carousel.image_url
                        }
                        alt="Hero Preview"
                        className="h-32 object-cover rounded-lg"
                        />
                    </div>
                    )}
                </div>

                </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdmincarouselEditorPage;